import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

const ROLE_ALIASES = {
  'super-admin': 'super_admin',
  'super_admin': 'super_admin',
  'admin': 'super_admin',
  'superadmin': 'super_admin',
  'super_administrator': 'super_admin',
  'teacher': 'teacher',
  'parent': 'parent',
  'student': 'student',
};

const normalizeUser = (user) => {
  if (!user) return user;
  const rawRole = user.role || '';
  const normalized = ROLE_ALIASES[rawRole] || rawRole.replace(/-/g, '_');
  return {
    ...user,
    role: normalized,
  };
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      const { access_token, refresh_token, user } = response.data;
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      return { accessToken: access_token, refreshToken: refresh_token, user, permissions: user?.permissions || [] };
    } catch (error) {
      const message = getErrorMessage(error, 'Login failed');
      return rejectWithValue(message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      const { access_token, refresh_token, user } = response.data;
      return { accessToken: access_token, refreshToken: refresh_token, user, permissions: user?.permissions || [] };
    } catch (error) {
      const message = getErrorMessage(error, 'Registration failed');
      return rejectWithValue(message);
    }
  }
);

export const loginByRole = createAsyncThunk(
  'auth/loginByRole',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.loginByRole(credentials);
      const { access_token, refresh_token, user } = response.data;
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      return { accessToken: access_token, refreshToken: refresh_token, user, permissions: user?.permissions || [] };
    } catch (error) {
      const message = getErrorMessage(error, 'Login failed');
      return rejectWithValue(message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }
);

export const loadCurrentUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return rejectWithValue('No token');
      const response = await authAPI.loadUser();
      return response.data;
    } catch (error) {
      return rejectWithValue('Session expired');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Update failed'));
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  'auth/changePassword',
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.changePassword(data);
      return { success: true };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Password change failed'));
    }
  }
);

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  permissions: [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = normalizeUser(action.payload);
      state.isAuthenticated = true;
    },
    updateUserField: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = normalizeUser(action.payload.user);
        state.isAuthenticated = true;
        state.permissions = action.payload.permissions || [];
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginByRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginByRole.fulfilled, (state, action) => {
        state.loading = false;
        state.user = normalizeUser(action.payload.user);
        state.isAuthenticated = true;
        state.permissions = action.payload.permissions || [];
        state.error = null;
      })
      .addCase(loginByRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = normalizeUser(action.payload.user);
        state.isAuthenticated = true;
        state.permissions = action.payload.permissions || [];
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.error = null;
      })
      .addCase(loadCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = normalizeUser(action.payload);
        state.isAuthenticated = true;
        state.permissions = action.payload.permissions || [];
      })
      .addCase(loadCurrentUser.rejected, (state) => {
        state.loading = false;
        if (!state.isAuthenticated) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          state.user = null;
          state.isAuthenticated = false;
          state.permissions = [];
        }
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = normalizeUser(action.payload);
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setUser, updateUserField } = authSlice.actions;
export default authSlice.reducer;
